import React from 'react';
import { decisionFromServiceabilityFlag } from '../utils/serviceabilityDecision';
import '../styles/tooltip.css';

/**
 * BuildingTooltipCard Component
 *
 * Displays building validation metrics in a clean, structured tooltip
 * Used for showing building serviceability status and distance metrics
 */
const STATUS_PRESENTATION = {
  serviceable: {
    headerClass: '',
    title: '✓ ساختمان قابل سرویس‌دهی',
    subtitle: 'این ساختمان شرایط لازم برای نصب فیبر را دارد',
    badgeClass: 'success',
    badgeLabel: 'قابل نصب فیبر',
  },
  not_serviceable: {
    headerClass: 'non-serviceable',
    title: '✗ ساختمان غیرقابل سرویس‌دهی',
    subtitle: 'این ساختمان شرایط لازم را ندارد',
    badgeClass: 'error',
    badgeLabel: 'غیرقابل نصب فیبر',
  },
  unknown: {
    headerClass: 'unknown-serviceability',
    title: 'وضعیت سرویس‌دهی ساختمان نامشخص است',
    subtitle: 'اطلاعات کافی برای اعلام نتیجهٔ قطعی در دسترس نیست',
    badgeClass: 'warning',
    badgeLabel: 'نیازمند بررسی',
  },
};

const BuildingTooltipCard = ({ validationData, onClose }) => {
  if (!validationData) {
    return null;
  }

  const decision = decisionFromServiceabilityFlag(validationData.serviceable);
  const status = STATUS_PRESENTATION[decision];
  const rules = validationData.rules || {};
  const reasons = validationData.reasons || [];

  return (
    <div className="building-tooltip-card">
      {/* Header */}
      <div className={`building-tooltip-header ${status.headerClass}`}>
        {/* Close Button */}
        {onClose && (
          <button
            className="tooltip-close-btn"
            onClick={onClose}
            aria-label="بستن"
          >
            ×
          </button>
        )}
        <h3 className="building-tooltip-title">{status.title}</h3>
        <p className="building-tooltip-subtitle">{status.subtitle}</p>
      </div>

      {/* Body */}
      <div className="building-tooltip-body">
        {/* Distance Metrics Section */}
        {(rules.rule1 || rules.rule2 || rules.rule5) && (
          <div className="building-tooltip-section">
            <h4 className="building-tooltip-section-title">📏 اطلاعات فاصله</h4>
            <table className="building-tooltip-metrics">
              <tbody>
                {/* Euclidean Distance (Rule 1) */}
                {rules.rule1 && rules.rule1.distance !== undefined && (
                  <tr>
                    <td>فاصله اقلیدسی:</td>
                    <td>
                      <span className={`distance-badge ${rules.rule1.passed ? 'success' : 'error'}`}>
                        {Math.round(rules.rule1.distance)}m
                      </span>
                    </td>
                  </tr>
                )}

                {/* Network Distance (Rule 2) */}
                {rules.rule2 && rules.rule2.networkDistance !== undefined && (
                  <tr>
                    <td>فاصله شبکه:</td>
                    <td>
                      <span className={`distance-badge ${rules.rule2.passed ? 'success' : 'error'}`}>
                        {Math.round(rules.rule2.networkDistance)}m
                      </span>
                    </td>
                  </tr>
                )}

                {/* Drop Length (Rule 5) */}
                {rules.rule5 && rules.rule5.dropLength !== undefined && (
                  <tr>
                    <td>طول دراپ:</td>
                    <td>
                      <span className={`distance-badge ${rules.rule5.passed ? 'success' : 'error'}`}>
                        {Math.round(rules.rule5.dropLength)}m
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Street Crossing Section (Rule 4) */}
        {rules.rule4 && rules.rule4.crossings && rules.rule4.crossings.length > 0 && (
          <div className="building-tooltip-section">
            <h4 className="building-tooltip-section-title">🛣️ عبور از معابر</h4>
            <table className="building-tooltip-metrics">
              <tbody>
                <tr>
                  <td>تعداد عبور:</td>
                  <td>
                    <span className={`distance-badge ${rules.rule4.passed ? 'success' : 'warning'}`}>
                      {rules.rule4.crossings.length} معبر
                    </span>
                  </td>
                </tr>
                {rules.rule4.maxCrossingWidth !== undefined && (
                  <tr>
                    <td>پهن‌ترین معبر:</td>
                    <td>
                      <span className={`distance-badge ${rules.rule4.passed ? 'success' : 'error'}`}>
                        {rules.rule4.maxCrossingWidth.toFixed(1)}m
                      </span>
                    </td>
                  </tr>
                )}
                {rules.rule4.widestCrossing && rules.rule4.widestCrossing.roadName && (
                  <tr>
                    <td>نام معبر:</td>
                    <td className="road-name-cell">
                      {rules.rule4.widestCrossing.roadName}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Reasons Section (only for non-serviceable buildings) */}
        {decision === 'not_serviceable' && reasons.length > 0 && (
          <div className="building-tooltip-section">
            <hr className="tooltip-divider" />
            <h4 className="building-tooltip-section-title">❌ دلایل عدم سرویس‌دهی</h4>
            <ul className="tooltip-reasons-list">
              {reasons.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Status Badge */}
        <div className="building-tooltip-section" style={{ marginTop: '14px' }}>
          <div className={`status-badge ${status.badgeClass}`}>
            <span className="tooltip-icon">
              {decision === 'serviceable' && (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {decision === 'not_serviceable' && (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              {decision === 'unknown' && <span aria-hidden="true">؟</span>}
            </span>
            <span>{status.badgeLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildingTooltipCard;
